/**
 * Single location-state abstraction for routing reads in coreapp.
 *
 * Standalone: setters are never called; reads pass through to window.location
 * directly — zero-overhead, behavior identical to reading window.location.
 *
 * MFE: useMfeRouter calls setPathname/setSearch on every memory-router state
 * change. Reads then return the embedded app's URL instead of the host's URL
 * that window.location reflects.
 *
 * Replaces N per-function monkey-patches in mfe/src/mount.tsx — coreapp
 * functions that need routing state read through this abstraction once,
 * MFE swaps the source once.
 */
let _pathname: string | null = null;
let _search: string | null = null;

export const LocationSource = {
  getPathname: (): string => _pathname ?? window.location.pathname,
  getSearch: (): string => _search ?? window.location.search,
  /** Full href reconstructed from pathname+search when MFE has set them.
   *  Standalone falls through to window.location.href so origin/hash are preserved. */
  getHref: (): string => {
    if (_pathname === null && _search === null) return window.location.href;
    return (
      window.location.origin +
      (_pathname ?? window.location.pathname) +
      (_search ?? window.location.search)
    );
  },
  setPathname: (p: string | null): void => {
    _pathname = p;
  },
  setSearch: (s: string | null): void => {
    _search = s;
  }
};
